import { logger } from './Logger';
/**
 * Simple file logger for trap mechanics debugging
 * Writes all trap-related logs to a separate file (server-side only)
 */
export class TrapLogger {
    static instance;
    logFilePath = '';
    writeStream = null;
    isServerSide;
    constructor() {
        // Check if we're on server side (Node.js)
        const global = globalThis;
        this.isServerSide =
            typeof globalThis === 'object' &&
                global.window === undefined &&
                typeof global.process !== 'undefined';
        if (this.isServerSide) {
            try {
                // Dynamic imports for Node.js modules (only on server)
                const fs = require('fs');
                const path = require('path');
                const process = globalThis.process;
                // Create logs directory if it doesn't exist
                const logsDir = path.join(process.cwd(), 'logs');
                if (!fs.existsSync(logsDir)) {
                    fs.mkdirSync(logsDir, { recursive: true });
                }
                // Create log file with timestamp
                const timestamp = new Date().toISOString().replace(/:/g, '-').split('.')[0];
                this.logFilePath = path.join(logsDir, `trap-analysis-${timestamp}.log`);
                // Initialize write stream
                this.writeStream = fs.createWriteStream(this.logFilePath, { flags: 'a' });
                // Write header
                this.writeToFile('='.repeat(80));
                this.writeToFile(`TRAP ANALYSIS LOG - Started at ${new Date().toISOString()}`);
                this.writeToFile('='.repeat(80));
            }
            catch (error) {
                logger.warn('TrapLogger: File logging not available in this environment');
                this.isServerSide = false;
            }
        }
    }
    static getInstance() {
        if (!TrapLogger.instance) {
            TrapLogger.instance = new TrapLogger();
        }
        return TrapLogger.instance;
    }
    writeToFile(message) {
        if (this.isServerSide && this.writeStream) {
            this.writeStream.write(`${message}\n`);
        }
    }
    /**
     * Log a trap-related message
     * Also outputs to console for immediate visibility
     */
    log(message, data) {
        const timestamp = new Date().toISOString();
        const formattedMessage = `[${timestamp}] ${message}`;
        // Write to file
        this.writeToFile(formattedMessage);
        // If there's additional data, stringify and log it
        if (data !== undefined) {
            const dataStr = typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data);
            this.writeToFile(`  DATA: ${dataStr}`);
        }
        // Also log using logger
        const context = {
            module: 'TrapLogger',
            data,
        };
        logger.debug(formattedMessage, context);
    }
    /**
     * Log a separator for better readability
     */
    separator(title) {
        const sep = '='.repeat(50);
        if (title) {
            const formattedTitle = ` ${title} `;
            const padLength = Math.floor((50 - formattedTitle.length) / 2);
            const paddedTitle = '='.repeat(padLength) + formattedTitle + '='.repeat(padLength);
            this.writeToFile(paddedTitle);
            logger.debug(paddedTitle, { module: 'TrapLogger' });
        }
        else {
            this.writeToFile(sep);
            logger.debug(sep, { module: 'TrapLogger' });
        }
    }
    /**
     * Log trap analysis start
     */
    logAnalysisStart(position, trappedSide, round, moves) {
        this.separator('TRAP ANALYSIS START');
        this.log(`Position: ${position}`);
        this.log(`Trapped side: ${trappedSide}`);
        this.log(`Round: ${round}, Moves this round: ${moves}`);
    }
    /**
     * Log trap analysis end
     */
    logAnalysisEnd(situation, result) {
        this.log(`Situation determined: ${situation}`);
        this.log('Result:', result);
        this.separator('TRAP ANALYSIS END');
    }
    /**
     * Close the logger
     */
    close() {
        if (this.isServerSide && this.writeStream) {
            this.writeToFile('='.repeat(80));
            this.writeToFile(`TRAP ANALYSIS LOG - Ended at ${new Date().toISOString()}`);
            this.writeToFile('='.repeat(80));
            this.writeStream.end();
            this.writeStream = null;
        }
    }
}
// Export a singleton instance for easy access
export const trapLogger = TrapLogger.getInstance();
//# sourceMappingURL=TrapLogger.js.map